import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Link } from '@sorare/routing';

import type { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { isType } from '@sorare/core/src/lib/gql';

import { DumbSo5Reward } from './DumbSo5Reward/index.tsx';
import type { Reward_userAccountEntry } from './__generated__/index.graphql.ts';

type Props = {
  userAccountEntry: Reward_userAccountEntry;
  primaryCurrency: Currency;
};

export const Reward = ({ userAccountEntry, primaryCurrency }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { tokenOperation } = userAccountEntry;

  if (
    !isType(tokenOperation, 'So5Reward') &&
    !isType(tokenOperation, 'TokenMonetaryReward')
  ) {
    return null;
  }

  if (isType(tokenOperation, 'So5Reward') && tokenOperation.so5Ranking) {
    const { so5Leaderboard, so5Lineup } = tokenOperation.so5Ranking;

    return (
      <DumbSo5Reward
        userAccountEntry={userAccountEntry}
        primaryCurrency={primaryCurrency}
      >
        <Link
          to={generateSportContextPath(
            ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER,
            {
              params: {
                fixture: so5Leaderboard.so5Fixture.slug,
                eventType: so5Leaderboard.so5Fixture.type.toLowerCase(),
                contender: so5Lineup.so5LeaderboardContender.slug,
              },
            }
          )}
        >
          <LabelM as="p">
            <FormattedMessage
              id="TransactionsHistoryReward.details"
              defaultMessage="Finished #{ranking} of {leaderboard} - {gameWeek}"
              values={{
                ranking: tokenOperation.so5Ranking.ranking,
                leaderboard: so5Leaderboard.displayName,
                gameWeek: so5Leaderboard.so5Fixture.longDisplayName,
              }}
            />
          </LabelM>
        </Link>
      </DumbSo5Reward>
    );
  }

  return (
    <DumbSo5Reward
      userAccountEntry={userAccountEntry}
      primaryCurrency={primaryCurrency}
    />
  );
};

Reward.fragments = {
  userAccountEntry: gql`
    fragment Reward_userAccountEntry on UserAccountEntry {
      id
      ...DumbSo5Reward_userAccountEntry
      tokenOperation {
        ... on TokenMonetaryReward {
          id
        }
        ... on So5Reward {
          slug
          so5Ranking {
            id
            ranking
            so5Lineup {
              id
              so5LeaderboardContender {
                slug
              }
            }
            so5Leaderboard {
              slug
              displayName
              so5Fixture {
                slug
                type
                longDisplayName
              }
            }
          }
        }
      }
    }
    ${DumbSo5Reward.fragments.userAccountEntry}
  ` as TypedDocumentNode<Reward_userAccountEntry>,
};
